import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { find, map, range as _range, remove as _remove, some } from 'lodash-es';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { CampaignV1ApiService } from 'apps/account/src/account-shared/services/campaign-v1-api.service';
import { IncentivesV1ApiService } from 'apps/account/src/account-shared/services/incentives-v1-api.service';
import { SettingsLanguagesV1ApiService } from 'apps/account/src/account-shared/services/settings-languages-v1-api.service';
import { DEFAULT_LANGUAGE, languageHelper } from 'apps/account/src/account-shared/services/language-helper.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtLanguageSelectorComponent } from '../contest-sheets/ft-language-selector.component';
import { FtCampaignGoalsComponent } from './ft-campaign-goals.component';
import { FtEditCampaignGoalsComponent, EditGoalsTitlePipe } from './ft-edit-campaign-goals.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { FtFieldEditorModalComponent, FieldEditorModalConfig } from '../contest-sheets/field-editor-modal.component';
import { FtHtmlFieldComponent } from '../contest-sheets/ft-html-field.component';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtDateRangeDirective } from './ft-date-range.directive';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';
import dayjs from 'dayjs/esm/index';

/**
 * Native port of the AngularJS pre-stay campaign editor (the heaviest single editor). Deferred-save:
 * a hidden {@link FtPendingChangesPanelComponent} (`[live]=model.campaign`) commits the whole campaign;
 * each field editor (name / criteria / share-message / goals) mutates `model.campaign` and nudges the
 * panel via `panel.recompute()`, and each Cancel reverts its open-time snapshot. The Save-enable rule
 * is reproduced in {@link campaignValid}. Handles both editing and creation; loads its own data
 * (Campaign.get + Incentives.getAvailableIncentives).
 */
@Component({
    selector: 'ft-campaign-prestay-ng',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        FtLanguageSelectorComponent,
        FtCampaignGoalsComponent,
        FtEditCampaignGoalsComponent,
        EditGoalsTitlePipe,
        FtPendingChangesPanelComponent,
        FtFieldEditorModalComponent,
        FtHtmlFieldComponent,
        FtModalComponent,
        FtModalChromeComponent,
        FtDateRangeDirective,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-campaign-prestay.component.scss'],
    templateUrl: './ft-campaign-prestay.component.html',
    host: {
        '(document:click)': 'onDocumentClick()',
    },
})
export class FtCampaignPrestayComponent implements OnInit, OnChanges {
  readonly campaignId = input.required<string>();
  readonly layerName = input('pre-stay');
  readonly propertyUuid = input.required<string>();
  readonly isCreating = input(false);

  /**
   * Emitted after a successful Campaign.create (the v1 `campaignCreatedCallback`). The container flips
   * `campaignUuid`/`isCreating` so this same instance re-activates in editing mode for the new campaign.
   */
  readonly created = output<any>();
  /** Emitted by the create-alert Cancel (the v1 `campaignCreateCancelCallback` → close the container). */
  readonly requestClose = output<void>();

  readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

  protected readonly model = signal<any>({ campaign: undefined, languages: [], stayLengths: [], campaignType: undefined, isCreating: false });
  /** The per-language preview selection (the v1 controller `$scope.selectedLanguage`). */
  protected readonly selectedLanguage = signal<any>({});
  /** The available incentives (loaded; bound into the goals modal). */
  protected readonly propertyIncentives = signal<any[]>([]);
  /** Set by onEditGoals (native read-view click) → bound by the native goals modal. */
  protected readonly types = signal<number[]>([]);
  protected readonly incentiveTag = signal<any>(undefined);

  /** INFO name field-editor config (null = closed). */
  protected readonly nameFieldConfig = signal<FieldEditorModalConfig | null>(null);

  /** SHARE MESSAGE modal open flag + its messages snapshot (v1 ft-snapshot rollback). */
  protected readonly messageModalOpen = signal(false);
  private messagesSnapshot: any = null;
  protected readonly messageSwitchUid = 'ft-prestay-msg-switch';
  protected readonly messageFields = ['text'];
  protected readonly messageFieldsOptions = { mode: 'text', tokens: ['property', 'city'] };

  /** CRITERIA modal open flag + its model.campaign snapshot. */
  protected readonly criteriaModalOpen = signal(false);
  private criteriaSnapshot: any = null;
  /**
   * v1 editCriteriaModal date-range "add" options.
   *
   * `showYearInHeader: false`, `showDaysOfWeek: false` and `showFullMonthInHeader: true` used to sit
   * here too. They were DEAD: none of the three exists anywhere in bootstrap-daterangepicker (grep
   * exit 1) and no stylesheet achieved them either, so the calendar has always shown the year, the
   * weekday row and an abbreviated month name. Dropped rather than reimplemented — the code plainly
   * INTENDED to hide the year and the days-of-week, so whether this calendar should look that way is
   * a live design question, raised on its own rather than answered silently inside a dependency
   * retirement.
   *
   * `format: 'MMMM D'` carries no year token, which is what puts FtDaterangeCalendar into its
   * `yearless` mode — month/day only, leap-year pinned, wrap-aware.
   */
  protected readonly addRangeOptions = { format: 'MMMM D' };

  /** GOALS modal open flag + its goals.item snapshot. */
  protected readonly goalsModalOpen = signal(false);
  private goalsSnapshot: any = null;

  /** The v1 controller-private `selectedDateRange` (criteria date-range editing). */
  private selectedDateRange: any;
  /** A single open-dropdown pointer for the criteria stay-length / language dropdowns (v1 ft-click-class). */
  private openDropdownKind: string | null = null;
  private openDropdownRef: any = null;
  /** layerName with the dash removed — the API form ('prestay'). */
  private layerNameApi = 'prestay';

  // WS5 #10 inc 2s: FULLY drained — Campaign/Incentives/SettingsLanguages are the
  // native V1Api ports, languageHelper/_/dayjs the native util + the same module
  // instances the v1 services returned.
  private readonly Campaign = inject(CampaignV1ApiService);
  private readonly Incentives = inject(IncentivesV1ApiService);
  private readonly SettingsLanguages = inject(SettingsLanguagesV1ApiService);
  private readonly CampaignType: any = { None: 0, Custom: 30, AllGuests: 255 }; // v1 .constant('CampaignType')
  private readonly languageHelper = languageHelper;
  private readonly parse: any = inject(ParseService);
  private readonly dayjs: any = dayjs;
  // WS5 #10 inc 2i: 'campaign.changed' is native↔native (the only listener is the
  // native ft-campaign-container header) → the native event bus, not $rootScope.
  private readonly eventBus = inject(EventBusService);
  private readonly translateSvc = inject(TranslateService);
  private readonly translateFn: (key: string, params?: any) => string = (key: string, params?: any) => this.translateSvc.instant(key, params);
  private readonly zone = inject(NgZone);

  private _initialized = false;

  ngOnInit(): void {
    this._initialized = true;
    this.reset();
    this.activate();
  }

  /**
   * WS5 Phase 3 #8a: after a create, the container flips campaignUuid + isCreating→false on THIS
   * persisting instance (nativeBranch stays 'prestay') — re-init in editing mode for the new campaign,
   * the native equivalent of the v1 adapter's teardownV1 + loadHeader re-mount. Guarded so the initial
   * input set (ngOnChanges fires before ngOnInit) doesn't double-activate.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this._initialized) return;
    if (changes['campaignId'] || changes['isCreating'] || changes['propertyUuid'] || changes['layerName']) {
      this.reset();
      this.activate();
    }
  }

  private reset(): void {
    this.layerNameApi = (this.layerName() || 'pre-stay').replace('-', '');
    this.model.set({
      campaignId: this.campaignId(),
      isCreating: !!this.isCreating(),
      campaignType: this.CampaignType,
      stayLengths: _range(1, 8),
      campaign: undefined,
      languages: [],
    });
  }

  // --- data load (CampaignController.activate / setupEditing / setupCreating) ---

  private activate(): void {
    if (this.model().isCreating) {
      // v1 setupCreating(): SettingsLanguages.get → emptyCampaign + type=Custom + default messages.
      Promise.resolve(this.SettingsLanguages.get({ propertyId: this.propertyUuid() })).then((data: any) =>
        this.zone.run(() => {
          this.model().campaign = this.Campaign.emptyCampaign();
          this.model().languages = data.languages.item;
          this.model().campaign.type = this.CampaignType.Custom;
          this.ensureMessages(this.model().campaign, this.model().languages);
          this.selectedLanguage.set(find(this.model().languages, { code: DEFAULT_LANGUAGE }) || this.model().languages[0] || {});
        }),
      );
    } else {
      Promise.resolve(this.Campaign.get({ id: this.campaignId(), layerName: this.layerNameApi, propertyUuid: this.propertyUuid() })).then((response: any) =>
        this.zone.run(() => {
          this.prepareCampaignEditResponse(response);
          this.selectedLanguage.set(find(this.model().languages, { code: DEFAULT_LANGUAGE }) || this.model().languages[0] || {});
        }),
      );
    }
    Promise.resolve(this.Incentives.getAvailableIncentives(this.propertyUuid())).then((response: any) =>
      this.zone.run(() => {
        this.propertyIncentives.set(response.data);
      }),
    );
  }

  private prepareCampaignEditResponse(response: any): void {
    this.model().campaign = response;
    this.model().languages = response.languages.item;
    this.ensureMessages(this.model().campaign, this.model().languages);
  }

  private ensureMessages(campaign: any, languages: any[]): void {
    if (!campaign.messages || !campaign.messages.length) {
      const message: any = {
        isActive: 1,
        languages: [{ code: 'en', text: 'Planning my upcoming trip - staying at {property}. Some perks for friends:', name: 'English' }],
      };
      this.languageHelper.merge(message.languages, languages);
      campaign.messages = [message];
    }
  }

  // --- read-view derived values ---

  protected get isCustomOrCreating(): boolean {
    return this.model().campaign?.type == this.model().campaignType?.Custom || !!this.model().isCreating;
  }

  protected get dateRanges(): any[] {
    return this.model().campaign?.criteria?.dateRanges?.item || [];
  }
  protected get stayLengths(): any[] {
    return this.model().campaign?.criteria?.stayLengths?.item || [];
  }
  protected get roomTypesText(): string {
    return (this.model().campaign?.criteria?.roomTypes?.item || []).map((r: any) => r.type).join(', ');
  }
  protected get criteriaLanguagesText(): string {
    return (this.model().campaign?.criteria?.languages?.item || []).map((l: any) => l.name).join(', ');
  }
  protected get rateCodesText(): string {
    return (this.model().campaign?.criteria?.rateCodes?.item || []).map((r: any) => r.code).join(', ');
  }

  /** messages | orderBy:'isActive':true (active first). */
  protected get orderedMessages(): any[] {
    const msgs = this.model().campaign?.messages || [];
    return [...msgs].sort((a: any, b: any) => (this.parseBool(b.isActive) ? 1 : 0) - (this.parseBool(a.isActive) ? 1 : 0));
  }

  /** (message.languages | arrayItem:'code':selectedLanguage.code).text */
  protected messageText(message: any): string {
    const lang = (message?.languages || []).find((l: any) => l.code === this.selectedLanguage()?.code);
    return lang ? lang.text : '';
  }

  protected parseBool(value: any): boolean {
    return this.parse.bool(value);
  }

  /** month filter (translate('...month' + n) when number / non-empty string). */
  protected monthName(n: any): string {
    if (typeof n === 'number' || (typeof n === 'string' && n)) {
      return this.translateFn('flipto.components.common.date.month' + n);
    }
    return '';
  }

  /** nights filter + lowercase. */
  protected nightsText(n: any): string {
    const night = this.translateFn('flipto.account.platform.preStay.campaign.Night');
    const nights = this.translateFn('flipto.account.platform.preStay.campaign.Nights');
    const moreThan7 = this.translateFn('flipto.account.platform.preStay.campaign.MoreThan7');
    let s: string;
    if (n === 1) s = n + ' ' + night;
    else if (n === 99) s = moreThan7 + ' ' + nights;
    else if (n === undefined) s = '';
    else s = n + ' ' + nights;
    return (s || '').toLowerCase();
  }

  // --- the 6 ng-forms validity (the panel `inactive` keystone) ---

  /**
   * v1 AngularJS `ngModelController.$isEmpty` (the ng-required rule): a value is EMPTY iff
   * undefined / '' / null / NaN (`v !== v`). NOTE: boolean `false` is a FILLED value (NOT empty),
   * and NaN IS empty — reproduced exactly so native validity matches the v1 forms for every shape.
   */
  private req(v: any): boolean {
    return !(v === undefined || v === '' || v === null || v !== v);
  }

  /** Reproduces the AngularJS `filter:{type:t, isActive:1}[0]` on goals (loose substring comparator). */
  private firstActiveGoal(type: number): any {
    const goals = (this.model().campaign?.goals?.item || []) as any[];
    return goals.filter((g: any) => this.looseMatch(g?.type, type) && this.looseMatch(g?.isActive, 1))[0];
  }

  /** AngularJS default filter comparator for primitives: lowercased-string `indexOf`. */
  private looseMatch(actual: any, expected: any): boolean {
    if (actual === undefined) return false;
    return ('' + actual).toLowerCase().indexOf(('' + expected).toLowerCase()) !== -1;
  }

  /** v1 `campaignForm.$valid`. */
  private campaignFormValid(): boolean {
    const c = this.model().campaign;
    if (!c) return false;
    // name required when Custom
    if (c.type == this.model().campaignType.Custom && !this.req(c.name)) return false;
    // messages length: v1 `<input type=number ng-model=messages.length min=1>` (NO required) — an
    // undefined messages bypasses the min validator (valid); an empty [] is length 0 < 1 (invalid).
    const messages = c.messages;
    if (messages != null && messages.length < 1) return false;
    // each message's English text required
    for (const m of messages || []) {
      const en = (m.languages || []).find((l: any) => l.code === 'en');
      if (!en || !this.req(en.text)) return false;
    }
    // active type-1 + type-2 goal incentiveUUID required
    const t1 = this.firstActiveGoal(1);
    if (!t1 || !this.req(t1.incentiveUUID)) return false;
    const t2 = this.firstActiveGoal(2);
    if (!t2 || !this.req(t2.incentiveUUID)) return false;
    return true;
  }

  /** v1: any of the 5 criteria*Form.$valid (each valid iff its FIRST item has all required fields). */
  private criteriaFormsAnyValid(): boolean {
    const cr = this.model().campaign?.criteria || {};
    const first = (k: string) => ((cr[k] && cr[k].item) || [])[0];
    const sl = first('stayLengths');
    if (sl && this.req(sl.minNights) && this.req(sl.maxNights)) return true;
    const dr = first('dateRanges');
    if (dr && this.req(dr.startMonth) && this.req(dr.startDay) && this.req(dr.endMonth) && this.req(dr.endDay)) return true;
    const rc = first('rateCodes');
    if (rc && this.req(rc.code)) return true;
    const rt = first('roomTypes');
    if (rt && this.req(rt.type)) return true;
    const lg = first('languages');
    if (lg && this.req(lg.code)) return true;
    return false;
  }

  /** The editing panel `inactive` = !(campaignForm && (AllGuests || any criteria)). */
  protected campaignValid(): boolean {
    if (!this.campaignFormValid()) return false;
    if (this.model().campaign?.type == this.model().campaignType.AllGuests) return true;
    return this.criteriaFormsAnyValid();
  }

  /** The isCreating alert `inactive` = !(campaignForm && any criteria) (NO AllGuests exemption). */
  protected createValid(): boolean {
    return this.campaignFormValid() && this.criteriaFormsAnyValid();
  }

  // --- INFO name editor ---

  protected editName(): void {
    this.panel()?.setupIfNeeded();
    this.nameFieldConfig.set({
      type: 'text',
      datasource: this.model().campaign,
      fields: ['name'],
      title: this.translateFn('flipto.account.VariationName'),
      // No onCancel: the field-editor shell reverts its own per-field snapshot (v1 ft-snapshot).
    });
  }

  protected onNameClosed(): void {
    this.nameFieldConfig.set(null);
    this.panel()?.recompute();
  }

  // --- SHARE MESSAGE editor ---

  protected editMessage(message: any): void {
    this.panel()?.setupIfNeeded();
    this.model().selectedMessage = message;
    this.model().editingNewMessage = false;
    this.messagesSnapshot = snapshotClone(this.model().campaign.messages);
    this.messageModalOpen.set(true);
  }

  protected addMessage(): void {
    // Snapshot the panel baseline BEFORE pushing the new message (v1 snapshots model.campaign at LOAD,
    // not lazily). Otherwise the lazy setupIfNeeded inside editMessage would baseline AFTER the push,
    // and add-then-cancel would remove a message the baseline contained → a phantom pending change.
    this.panel()?.setupIfNeeded();
    const newMessage = {
      isActive: true,
      languages: map(this.model().languages, (language: any) => ({ name: language.name, code: language.code, text: '' })),
    };
    this.model().campaign.messages.push(newMessage);
    this.editMessage(newMessage);
    this.model().editingNewMessage = true;
  }

  /** v1 ng-hide for the switch + delete: isActive (raw) && active-count (filter:{isActive:true}) <= 1. */
  protected messageSwitchHidden(): boolean {
    const m = this.model().selectedMessage;
    if (!m || !m.isActive) return false;
    const activeCount = (this.model().campaign?.messages || []).filter((x: any) => this.looseMatch(x.isActive, true)).length;
    return activeCount <= 1;
  }

  protected onMessageActiveToggle(ev: Event): void {
    if (this.model().selectedMessage) this.model().selectedMessage.isActive = (ev.target as HTMLInputElement).checked;
  }

  protected onMessageDone(): void {
    // The edits persist on model.campaign.messages → the panel detects via native-diff.
    this.messageModalOpen.set(false);
    this.messagesSnapshot = null;
    this.panel()?.recompute();
  }

  protected onMessageCancel(): void {
    // v1 modal cancel order: ft-snapshot rollback FIRST, THEN on-cancel="editMessageCancel". At
    // rollback time the messages array still CONTAINS the just-added message (the snapshot was taken
    // after addMessage pushed it), so the diff is text-only — never a root-level array delete. Then
    // editMessageCancel drops the just-added message. (Doing remove-then-revert would let revertDiffs
    // RE-ADD the just-removed message — the homepage-trap-adjacent bug the review caught.)
    const live = this.model().campaign?.messages;
    if (live && this.messagesSnapshot) {
      revertDiffs(live, this.messagesSnapshot, computeDiffs(this.messagesSnapshot, live));
    }
    this.editMessageCancel();
    this.messageModalOpen.set(false);
    this.messagesSnapshot = null;
    this.panel()?.recompute();
  }

  private editMessageCancel(): void {
    if (this.model().editingNewMessage) {
      _remove(this.model().campaign.messages, (m: any) => m === this.model().selectedMessage);
      this.model().editingNewMessage = false;
    }
  }

  protected deleteMessage(message: any): void {
    _remove(this.model().campaign.messages, (m: any) => m === message);
    // v1 deleteMessage → closeMessageEdit() = modal done() (the removal persists).
    this.messageModalOpen.set(false);
    this.messagesSnapshot = null;
    this.panel()?.recompute();
  }

  // --- CRITERIA editor ---

  protected showCriteriaModal(): void {
    this.panel()?.setupIfNeeded();
    this.criteriaSnapshot = snapshotClone(this.model().campaign);
    this.criteriaModalOpen.set(true);
    // v1 ftModal focused the modal's first input on show (focusFirstInput default
    // true) — here the room-type input, which paints the criteria-editor
    // `input:focus` background. Timeout lets the @if content render first (v1
    // used $timeout the same way).
    setTimeout(() => document.querySelector<HTMLInputElement>('.modal-panel.criteria-editor input')?.focus());
  }

  /**
   * v1 modal_v1.html gated Done on `modalForm.$invalid`, and the criteria modal carried
   * hidden `ng-required` inputs for each stay-length row's minNights/maxNights — a
   * half-filled stay-length range dims Done. AngularJS `required` accepts 0 (only
   * undefined/null/''/NaN are empty), so compare accordingly.
   */
  protected criteriaDoneDisabled(): boolean {
    const rows = this.model()?.campaign?.criteria?.stayLengths?.item || [];
    const empty = (v: any) => v == null || v === '' || (typeof v === 'number' && Number.isNaN(v));
    return rows.some((sl: any) => empty(sl.minNights) || empty(sl.maxNights));
  }

  protected onCriteriaDone(): void {
    this.criteriaModalOpen.set(false);
    this.criteriaSnapshot = null;
    this.openDropdownKind = null;
    this.openDropdownRef = null;
    this.panel()?.recompute();
  }

  protected onCriteriaCancel(): void {
    const live = this.model().campaign;
    if (live && this.criteriaSnapshot) {
      revertDiffs(live, this.criteriaSnapshot, computeDiffs(this.criteriaSnapshot, live));
    }
    this.criteriaModalOpen.set(false);
    this.criteriaSnapshot = null;
    this.openDropdownKind = null;
    this.openDropdownRef = null;
    this.panel()?.recompute();
  }

  protected reversed(arr: any): any[] {
    return Array.isArray(arr) ? [...arr].reverse() : [];
  }

  protected removeItemFrom(item: any, collection: any[]): void {
    collection.splice(collection.indexOf(item), 1);
  }

  protected selectDateRange(dateRange: any): void {
    this.selectedDateRange = dateRange;
  }

  /** v1 add-range: push a new {startMonth,startDay,endMonth,endDay} (date objects, NO convertToNative). */
  protected addNewRange(e: { start: any; end: any }): void {
    this.model().campaign.criteria.dateRanges.item.push({
      startMonth: e.start.month() + 1,
      startDay: e.start.date(),
      endMonth: e.end.month() + 1,
      endDay: e.end.date(),
    });
  }

  /** v1 update-range: extend the currently-selected range with the new month/day. */
  protected updateDateRange(e: { start: any; end: any }): void {
    if (!this.selectedDateRange) return;
    this.selectedDateRange.startMonth = e.start.month() + 1;
    this.selectedDateRange.startDay = e.start.date();
    this.selectedDateRange.endMonth = e.end.month() + 1;
    this.selectedDateRange.endDay = e.end.date();
  }

  /**
   * v1 edit-dropdown options (startDate/endDate as 'MMMM D' strings the plugin parses). MEMOISED per
   * dateRange + value-signature: returning a fresh object every change-detection would re-trigger the
   * FtDateRangeDirective ngOnChanges (options changed) on EVERY CD → re-init the jQuery picker →
   * slam an open calendar shut. v1's `options="{…{{interp}}…}"` only changes when the interpolated
   * dates change, so we rebuild the object ONLY when the dateRange's month/day actually change.
   */
  protected editRangeOptions(dateRange: any): any {
    const sig = `${dateRange.startMonth}|${dateRange.startDay}|${dateRange.endMonth}|${dateRange.endDay}`;
    const cached = this.rangeOptsCache.get(dateRange);
    if (cached && cached.sig === sig) return cached.opts;
    const opts = {
      format: 'MMMM D',
      convertToNative: false,
      // The three show*/hide* keys that used to sit here were dead — see addRangeOptions.
      startDate: this.toMonth(this.parse.intOrDefault(dateRange.startMonth) - 1) + ' ' + dateRange.startDay,
      endDate: this.toMonth(this.parse.intOrDefault(dateRange.endMonth) - 1) + ' ' + dateRange.endDay,
    };
    this.rangeOptsCache.set(dateRange, { sig, opts });
    return opts;
  }
  private readonly rangeOptsCache = new WeakMap<any, { sig: string; opts: any }>();

  /** v1 toMonth filter — 0-based month index → 'MMMM' (English month name the plugin format expects). */
  private toMonth(month: any): string {
    return this.dayjs().month(month).format('MMMM');
  }

  /** v1 stay-length min options: model.stayLengths | filter:lessThan(max) | filter:minNotExist. */
  protected minLengthOptions(stayLength: any): number[] {
    return (this.model().stayLengths as number[])
      .filter((value: number) => (stayLength.maxNights ? value <= stayLength.maxNights : true))
      .filter((value: number) => !some(this.model().campaign.criteria.stayLengths.item, { minNights: value }));
  }

  /** v1 stay-length max options: model.stayLengths | filter:moreThan(min) (the 99 option is in the template). */
  protected maxLengthOptions(stayLength: any): number[] {
    return (this.model().stayLengths as number[]).filter((value: number) => (stayLength.minNights ? value >= stayLength.minNights : true));
  }

  /** v1 languages add list: model.languages | filter:languageNotAdded. */
  protected addableLanguages(): any[] {
    return (this.model().languages || []).filter((language: any) => !some(this.model().campaign.criteria.languages.item, { code: language.code }));
  }

  // --- criteria dropdown open/close (reproduces v1 ft-click-class toggle-class="active") ---
  // v1 ftClickClass (account-shared ClickClassDirective) uses a document:click handler: a click on the
  // element/child TOGGLES the class; a click OUTSIDE removes it. We reproduce that: the toggle anchor
  // toggles + stops propagation (so the document handler below doesn't immediately re-close it); menu
  // items set the value + closeDropdowns() + stop propagation (clicking an option closes the dropdown,
  // matching v1's child-click-toggles-off); and ANY click that reaches document (i.e. outside the
  // toggle/items) closes the open dropdown.

  protected toggleDropdown(kind: string, ref: any, ev?: Event): void {
    ev?.stopPropagation();
    if (this.openDropdownKind === kind && this.openDropdownRef === ref) {
      this.openDropdownKind = null;
      this.openDropdownRef = null;
    } else {
      this.openDropdownKind = kind;
      this.openDropdownRef = ref;
    }
  }

  protected isDropdownOpen(kind: string, ref: any): boolean {
    return this.openDropdownKind === kind && this.openDropdownRef === ref;
  }

  protected closeDropdowns(): void {
    this.openDropdownKind = null;
    this.openDropdownRef = null;
  }

  /** v1 ftClickClass outside-click close: any document click not stopped by a toggle/item closes. */
  protected onDocumentClick(): void {
    if (this.openDropdownKind !== null) this.closeDropdowns();
  }

  // --- GOALS editor (the #72 post-stay recipe) ---

  protected onEditGoals(e: { types: number[]; incentiveTag: number }): void {
    this.types.set(e.types);
    this.incentiveTag.set(e.incentiveTag);
    if (this.model().campaign && !this.model().campaign.goals) this.model().campaign.goals = { item: [] };
    this.panel()?.setupIfNeeded();
    this.goalsSnapshot = snapshotClone(this.model().campaign?.goals?.item);
    this.goalsModalOpen.set(true);
  }

  protected onGoalsDone(): void {
    this.goalsModalOpen.set(false);
    this.panel()?.recompute();
  }

  protected onGoalsCancel(): void {
    const live = this.model().campaign?.goals?.item;
    if (live && this.goalsSnapshot) {
      revertDiffs(live, this.goalsSnapshot, computeDiffs(this.goalsSnapshot, live));
    }
    this.goalsModalOpen.set(false);
    this.panel()?.recompute();
  }

  // --- deferred save / panel ---

  protected reloadGoals(): void {
    this.activate();
  }

  protected onPanelSave(): void {
    this.commit();
  }

  protected onPanelCancel(): void {
    /* no-op: FtPendingChangesPanelComponent reverted model.campaign in place. */
  }

  protected onCreateSave(): void {
    // v1 fidelity: the bare ft-pending-changes `.save` click fires unconditionally (createValid only
    // toggles the dimmed `.disabled` class; `.ft-btn.disabled` has no pointer-events:none) → no guard.
    this.commit();
  }

  /** The pending-changes deferred-save (NEVER auto-triggered — it PUTs). */
  commit(): void {
    const params = { propertyUUID: this.propertyUuid(), layerName: this.layerNameApi, id: this.model().campaign.id };
    const campaign = this.model().campaign;
    if (this.model().isCreating) {
      Promise.resolve(this.Campaign.create(params, campaign)).then((response: any) =>
        this.zone.run(() => {
          // v1 commit: Campaign.create → campaignCreatedCallback(response) → broadcast('campaign.changed').
          // Emit the created campaign so the container flips to editing it (the native re-mount), then broadcast.
          this.created.emit(response);
          this.eventBus.emit('campaign.changed', campaign);
        }),
      );
    } else {
      Promise.resolve(this.Campaign.update(params, campaign))
        .then((response: any) =>
          this.zone.run(() => {
            this.prepareCampaignEditResponse(response);
            // v1 snapshot.retake() re-snapshots the CURRENT (new) campaign. Pass the fresh response
            // object explicitly — the panel's [live] input hasn't re-bound to it yet at this microtask
            // (CD runs after), so commit() must NOT snapshot the stale pre-save object (else phantom
            // pending-changes reappear on the next edit).
            this.panel()?.commit(this.model().campaign);
            this.eventBus.emit('campaign.changed', campaign);
          }),
        );
    }
  }

  protected cancel(): void {
    // v1 campaignCreateCancelCallback: close the container. (The native overlay is NOT an AngularJS
    // ftModal, so the old ftModal.hide('accountv2.CampaignContainer') was a no-op on the native mount.)
    this.requestClose.emit();
  }
}
